import { useEffect, useState } from "react";
import { fmtMoneyTrim } from "@/lib/fmt";

type BidCellProps = {
  value: number | string | null | undefined;
  editable: boolean;
  onCommit?: (newValue: number) => void;
};

/**
 * Keyword-row bid cell with inline edit. Click to enter edit mode,
 * Enter to commit, Escape or blur to close. Read-only variant shows a
 * muted value with a tooltip-free cursor.
 *
 * Renders a <td>; caller doesn't need to wrap. When committing, passes
 * the parsed number up — parsing is lenient (strips $, commas, spaces).
 *
 * Display uses `fmtMoneyTrim` — `$10` instead of `$10.00`, but
 * `$10.50` stays as is. Per user 2026-05-05: trailing `.00` reads as
 * noise on user-set values like daily_budget / bid / cpa_goal.
 *
 * Commit is no-op-guarded: if the parsed value equals the current
 * one (within 1e-9), we don't fire `onCommit` — prevents misleading
 * "X updated" toasts when the user clicks into the cell, looks at
 * the value, and clicks away without typing anything.
 */
export function BidCell({ value, editable, onCommit }: BidCellProps) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState<string>(formatLocal(value));

  useEffect(() => {
    // Пока ячейка в правке — не трогать набранное. Значение приходит
    // сверху не только после своей мутации: правило меняет ставку в
    // воркере, и с фоновым опросом (`pending-writes-tracker`) такое
    // изменение прилетает в открытую вкладку. Без этой проверки оно
    // затирает то, что человек печатает прямо сейчас.
    if (editing) return;
    setLocal(formatLocal(value));
  }, [value, editing]);

  if (!editable) {
    return (
      <td className="num">
        <span className="bid-cell readonly">${fmtMoneyTrim(coerceNumber(value))}</span>
      </td>
    );
  }

  const commit = () => {
    setEditing(false);
    // Accept both `,` and `.` as decimal separators (RU/EU users
    // type `1,7`; en-US users type `1.7`). Lookahead drops every
    // comma that's followed by THREE digits (a thousands grouping
    // boundary) — that way `1,234,567.89` → `1234567.89`. The
    // remaining lone comma, if any, becomes the decimal dot
    // (`1,7` → `1.7`). Final pass strips anything that isn't a
    // digit or a dot ($, spaces, etc.).
    const sanitised = local
      .replace(/\s/g, "")
      .replace(/,(?=\d{3})/g, "")
      .replace(",", ".")
      .replace(/[^0-9.]/g, "");
    const parsed = parseFloat(sanitised);
    if (Number.isNaN(parsed)) return;
    // No-op guard: if the input value rounds to the same number as
    // the current cell value, we treat the commit as a click-and-
    // walk-away with no change. Without this, the "Daily budget
    // updated on ASA" toast fires every time the user merely
    // inspects a cell.
    const original = coerceNumber(value);
    if (
      original !== null
      && Math.round(parsed * 100) === Math.round(original * 100)
    ) {
      return;
    }
    if (onCommit) onCommit(parsed);
  };

  return (
    <td className="num">
      {editing ? (
        <span className="bid-cell editing">
          <input
            type="text"
            autoFocus
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") {
                setLocal(formatLocal(value));
                setEditing(false);
              }
            }}
          />
        </span>
      ) : (
        <span className="bid-cell" onClick={() => setEditing(true)}>
          ${fmtMoneyTrim(coerceNumber(value))}
        </span>
      )}
    </td>
  );
}

function coerceNumber(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return v;
  const parsed = parseFloat(v);
  return Number.isNaN(parsed) ? null : parsed;
}

function formatLocal(v: number | string | null | undefined): string {
  // Edit-mode pre-fill matches the displayed format exactly — en-US
  // grouping (commas every three digits, dot for decimals). Easier to
  // read at a glance for >3-digit budgets. Commit's parseFloat is
  // safe: the regex strips everything except digits and the dot
  // before parsing.
  const n = coerceNumber(v);
  if (n === null) return "";
  const rounded = Math.round(n * 100) / 100;
  const isInteger = rounded === Math.trunc(rounded);
  return rounded.toLocaleString("en-US", {
    minimumFractionDigits: isInteger ? 0 : 2,
    maximumFractionDigits: 2,
  });
}
